import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { RenderElementProps, useSelected } from "slate-react";
import { useState } from "react";
import { useConfigurableTable } from './useConfigurableTable';
import { PreconfiguredField, usePreconfiguredFields } from '../CustomFieldsBlock/usePreconfiguredFields';
import { FormattedMessage } from 'react-intl';
import { usePowerEditorContext } from '../../slate/PowerEditorContext';
import { BlockWrapper } from '../../elements/Common';
import { Buttons, FormGrid, MoreActionsMenuButton, OccupyFreeSpace, SimpleDialog, Tooltip } from '../../../primitives';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { StrippedIconButton } from '../../../primitives/StrippedButtons';
import { Add, EditOutlined, ExpandMore, ListAlt, TableRows, UnfoldLess } from '@mui/icons-material';
import { PreconfiguredEditor } from './PreconfiguredEditor';
import isHotkey from 'is-hotkey';
import { useEditItem2 } from '../../../../api/useNewItem';
import { FormControlsForFields } from '../../../schemed';
import { FieldType, Schema } from '../../../../hooks/useSchema';
import { getBlockTitle } from '../CustomFieldsBlock';



export const ConfigurableTableBlockElementType = "configurable_table_block";


const EditRawDataSchema: Schema = {
  rows_json: { type: FieldType.textlong, label: " "}
}


export const ConfigurableTableBlockElement = (props: RenderElementProps) => {
  const { element, children } = props;
  
  const data = useConfigurableTable(element as any);
  const {
    rows,
    insertRow,
    updateRow,
    removeRow,
    updateCollapsed,
    moveRow,
    isCollapsed,
  } = data;
  const isSelected = useSelected();

  const preconfigured = usePreconfiguredFields((element as any).render_block_type, ConfigurableTableBlockElementType, (element as any).preconfigured_fields);
  const listFieldsIdsText = ((element as any).list_fields as string || "") || preconfigured.listFields || "";
  const listFields = useMemo(() => {
    const listFieldsIds = listFieldsIdsText.split(";").map(s => s.trim()).filter(s => !!s);
    if(!preconfigured.fields.length || !listFieldsIds.length) {
      return null;
    } else {
      const fields = listFieldsIds.map(id => preconfigured.fields.find(f => f.code === id)).filter(x => !!x) as PreconfiguredField[];

      return fields.length ? fields : null;
    }

  }, [preconfigured.fields, listFieldsIdsText]);

  const { viewMode } = usePowerEditorContext();

  const [dragging,setDragging] = useState<string | null>(null);
  const [dragTarget,setDragTarget] = useState<string | null>(null);

  const blockTitle = getBlockTitle(element, element as any);

  const editItemInPopup = useEditItem2<any>({
    save: async (item,changes) => {
      updateRow(item._id, changes);
      return item;
    }
  });

  const editRawData = useEditItem2<{ rows_json: string }>({
    save: async (item) => {
      try {
        const parsed = JSON.parse(item.rows_json);
        data.updateAllRows(parsed);
      } catch(e) {
      }
      return item;
    }
  })

  const startEditRawData = () => {
    editRawData.startEditing({
      rows_json: JSON.stringify(rows || [], null, 2),
    })
  }

  const rowActions = (r: any, idx: number): any => ([
    ["edit", <FormattedMessage id="common.edit"/>, () => editItemInPopup.startEditing({ ...r })],
    ["remove", <FormattedMessage id="common.delete"/>, () => removeRow(r._id)],
    ["row_before", <FormattedMessage id="powerdoc.plugins.table.insert.row_before"/>, () => insertRow(idx)],
    ["row_after", <FormattedMessage id="powerdoc.plugins.table.insert.row_after"/>, () => insertRow(idx+1)],
    ["duplicate", <FormattedMessage id="powerdoc.plugins.configurable_table_block.actions.duplicate_row"/>, () => data.duplicateRow(r._id)],
  ])

  const listFieldsToRender = listFields || preconfigured.fields;

  return (
    <div {...props.attributes}>
      {props.children}

      <Wrapper contentEditable={false} isSelected={isSelected}>
        {children}

        <Buttons style={{ paddingTop: 0 }}>
          {!!blockTitle &&
            <Typography variant="caption" color="textSecondary" onClick={() => updateCollapsed(false)}>{blockTitle}</Typography>}


          <OccupyFreeSpace />

          <Tooltip text_id="powerdoc.plugins.configurable_table_block.actions.edit_raw">
            <StrippedIconButton onClick={() => startEditRawData()}>
              <EditOutlined />
            </StrippedIconButton>
          </Tooltip>

          <Tooltip text_id={isCollapsed ? "common.show_details" : "common.hide_details"}>
            <StrippedIconButton onClick={() => updateCollapsed(!isCollapsed)}>
              {isCollapsed ? <ExpandMore /> : <UnfoldLess />}
            </StrippedIconButton>
          </Tooltip>
        </Buttons>

        {!isCollapsed && <>
          {!data.isForms && <TableContainer>
            <Table size="small">

              <TableHead>
                <TableCell className="idx" width={40}></TableCell>
                {!!listFieldsToRender.length && listFieldsToRender.map(f => (
                  <TableCell key={f.code} className={`ft-${f.type || ""}`}>
                    <Tooltip text={f.code}>
                      <>{f.label}</>
                    </Tooltip>
                  </TableCell>
                  
                ))}

                <TableCell width={40}></TableCell>
              </TableHead>

              <TableBody>
                {rows.map((r,idx) => (
                  <TableRow
                    key={r._id}
                    className={`tr ${r._id === dragging ? "dragged" : ""} ${r._id === dragTarget ? "drag-targetd" : ""}`}
                    draggable={!viewMode}
                    onDragStart={() => setDragging(r._id)}
                    onDragEnter={e => { e.preventDefault(); setDragTarget(r._id)}}
                    onDragOver={e => { e.preventDefault(); setDragTarget(r._id)}}
                    onDragLeave={e => { e.preventDefault(); setDragTarget(null)}}
                    onKeyUp={e => {
                      if(isHotkey("mod+Enter", e)) {
                        insertRow(idx+1);
                      }
                    }}
                    onDrop={e => {
                      e.stopPropagation();
                      if(dragging && r._id !== dragging) {
                        const idxFrom = rows.findIndex(rx => rx._id === dragging);
                        if(idxFrom >= 0 && idxFrom !== idx) {
                          moveRow(idxFrom, idx);
                        }
                      }
                      setDragging(null);
                      setDragTarget(null);
                    }}
                    >
                    <TableCell className="idx" width={40}>
                      <Typography variant="caption" color="textSecondary">{idx+1}</Typography>
                    </TableCell>

                    {!!listFieldsToRender.length && listFieldsToRender.map((f,fi) => (
                      <TableCell key={f.code} className={`ft-${f.type || ""}`}>
                        <PreconfiguredEditor
                          field={f}
                          schema={preconfigured.schema}
                          row={r}
                          updateRow={updateRow}
                          autoFocus={fi === 0 && data.insertedId === r._id}
                          />
                      </TableCell>
                      
                    ))}

                    <TableCell width={40} className="actions">
                      <MoreActionsMenuButton
                        actions={rowActions(r,idx)}
                        strippedButton
                        />
                    </TableCell>
                  </TableRow>
                ))}
                
              </TableBody>
            </Table>
          </TableContainer>}

          {!!data.isForms &&
            <FormGrid columns="1fr" className="rows-forms">

              {rows.map((r,idx) => (
                <FormGrid key={r._id} columns="max-content 1fr" noMargin className="row-form">

                  {!!preconfigured.fields.length && preconfigured.fields.map((f,fi) => (
                    <React.Fragment key={f.code}>
                      <Tooltip text={f.code}>
                        <Typography color="textSecondary">{f.label}</Typography>
                      </Tooltip>
                      <PreconfiguredEditor
                        field={f}
                        schema={preconfigured.schema}
                        row={r}
                        updateRow={updateRow}
                        autoFocus={fi === 0 && data.insertedId === r._id}
                        />
                    </React.Fragment>
                    
                  ))}

                  <Typography className="idx" variant="caption" color="textSecondary">{idx+1}</Typography>

                  <MoreActionsMenuButton
                    className="actions"
                    actions={rowActions(r,idx)}
                    strippedButton
                    />
                  </FormGrid>
                ))}
            </FormGrid>}
          
          <Buttons className="tail-actions">
            <StrippedIconButton onClick={() => insertRow()}><Add /></StrippedIconButton>
            <StrippedIconButton onClick={() => data.updateIsForms(!data.isForms)}>{data.isForms ? <TableRows /> : <ListAlt />}</StrippedIconButton>
            <StrippedIconButton onClick={() => updateCollapsed(true)}><UnfoldLess /></StrippedIconButton>
          </Buttons>
        </>}

      
      {!!editItemInPopup.item &&
        <SimpleDialog
          noFullscreen
          submitOnModEnter
          {...editItemInPopup.dialog}
          dialogTitle={<FormattedMessage id="common.edit" />}
          save={() => editItemInPopup.save()}>
            
            <PopupGrid columns="max-content 1fr" noMargin>

              {!!preconfigured.fields.length && preconfigured.fields.map((f,fi) => (
                <React.Fragment key={f.code}>
                  <Tooltip text={f.code}>
                    <Typography color="textSecondary" className="control-label">{f.label}</Typography>
                  </Tooltip>
                  <PreconfiguredEditor
                    field={f}
                    schema={preconfigured.schema}
                    row={editItemInPopup.item}
                    updateRow={(id,c) => editItemInPopup.update(c)}
                    autoFocus={fi === 0}
                    />
                </React.Fragment>
                
              ))}

            </PopupGrid>
        </SimpleDialog>}


        {!!editRawData.item &&
          <SimpleDialog
            noFullscreen
            submitOnModEnter
            maxWidth="lg"
            fullWidth
            {...editRawData.dialog}
            dialogTitle={<FormattedMessage id="common.edit" />}
            save={() => editRawData.save()}>
              
              <FormGrid columns="1fr" noMargin>
                <FormControlsForFields
                  fields={[
                    ["rows_json"]
                  ]}
                  data={editRawData.item}
                  onChange={(o,c) => editRawData.update(c)}
                  schema={EditRawDataSchema}
                  />
                

              </FormGrid>
          </SimpleDialog>}


      </Wrapper>
    </div>
  );
}



const Wrapper = styled(BlockWrapper)`
  
  & .tail-actions {
    width: 100%;
    justify-content: center;
  }

  & .tr {
    & .idx {
      vertical-align: top;
    }
    & .actions {
      opacity: 0;
    }
    &:hover {
      & .actions {
        opacity: 1;
      }
    }
  }

  & .row-form {
    position: relative;
    padding: 0.5rem 0.5rem;
    align-items: flex-end;

    &:nth-child(2n) {
      background: rgba(0,0,0,0.08);
    }
    &:nth-child(2n+1) {
      background: rgba(0,0,0,0.03);
    }

    & .idx {
      position: absolute;
      top: 2px;
      left: 0.5rem;
      transform-origin: top left;
      transform: scale(0.75);
    }
    & .actions {
      position: absolute;
      top: 2px;
      right: 0.5rem;
      opacity: 0;
    }
    &:hover {
      & .actions {
        opacity: 1;
      }
    }
  }

  & .medialib-picker {
    & img {
      width: 150px;
    }
    & .placeholder {
      width: 150px;
    }
  }

  & .ft-date {
    width: 160px;
  }
`;


const PopupGrid = styled(FormGrid)`
  .control-label {
    margin-top: 1em;
  }
`;
